// frontend/src/features/equipment/EquipmentSetupWizard/hooks/useSaveEquipment.ts
import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTank } from '@/contexts/TankContext';
import { equipmentApi } from '@/api/equipment';
import type { Equipment } from '@/types/equipment';
import { pendingToCreateBatch } from '../utils/equipmentMapping';
import type { PendingEquipment } from '../types';

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

export interface SaveResult {
  savedItems: Equipment[];
  savedTempIds: string[];
  error?: Error;
}

interface UseSaveEquipmentResult {
  /** Saves an array of pending equipment items to the API */
  saveEquipment: (pendingEquipment: PendingEquipment[]) => Promise<SaveResult>;
  /** Resets the hook state */
  reset: () => void;
  /** Whether the hook is in idle state (not started) */
  isIdle: boolean;
  /** Whether save is in progress */
  isSaving: boolean;
  /** Whether all items saved successfully */
  isSuccess: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error that occurred during save */
  error: Error | null;
  /** Number of items saved so far */
  savedCount: number;
  /** Total number of items to save */
  totalCount: number;
}

/**
 * Hook to save equipment from the wizard to the API.
 * Converts PendingEquipment to EquipmentCreate and saves sequentially.
 */
export function useSaveEquipment(): UseSaveEquipmentResult {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [savedCount, setSavedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const saveEquipment = useCallback(
    async (pendingEquipment: PendingEquipment[]): Promise<SaveResult> => {
      if (!selectedTank?.id) {
        const errorObj = new Error('No tank selected');
        setError(errorObj);
        setStatus('error');
        setSavedCount(0);
        setTotalCount(0);
        notifications.show({
          title: 'Error',
          message: errorObj.message,
          color: 'red',
        });
        return { savedItems: [], savedTempIds: [], error: errorObj };
      }

      let savedItems: Equipment[] = [];
      const savedTempIds: string[] = [];
      let totalItemsToCreate: number;

      try {
        totalItemsToCreate = pendingToCreateBatch(pendingEquipment).length;
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error('Failed to save equipment');
        setError(errorObj);
        setStatus('error');
        setSavedCount(0);
        setTotalCount(0);
        notifications.show({
          title: 'Error',
          message: errorObj.message,
          color: 'red',
        });
        return { savedItems, savedTempIds, error: errorObj };
      }

      setTotalCount(totalItemsToCreate);
      setSavedCount(0);
      setError(null);

      // Handle empty input
      if (totalItemsToCreate === 0) {
        setStatus('success');
        return { savedItems, savedTempIds };
      }

      setStatus('saving');

      for (const pendingItem of pendingEquipment) {
        const createdInPendingItem: Equipment[] = [];

        try {
          const pendingItemToCreate = pendingToCreateBatch([pendingItem]);

          for (const equipment of pendingItemToCreate) {
            const savedItem = await equipmentApi.create(selectedTank.id, equipment);
            createdInPendingItem.push(savedItem);
            savedItems.push(savedItem);
            setSavedCount((prev) => prev + 1);
          }

          savedTempIds.push(pendingItem.tempId);
        } catch (err) {
          if (createdInPendingItem.length > 0) {
            const rollbackResults = await Promise.allSettled(
              createdInPendingItem.map(async (createdItem) => {
                await equipmentApi.delete(selectedTank.id, createdItem.id);
                return createdItem.id;
              })
            );

            const rolledBackIds = new Set<string>();
            rollbackResults.forEach((result) => {
              if (result.status === 'fulfilled') {
                rolledBackIds.add(result.value);
              }
            });

            if (rolledBackIds.size > 0) {
              savedItems = savedItems.filter((savedItem) => !rolledBackIds.has(savedItem.id));
              setSavedCount(savedItems.length);
            }
          }

          const errorObj = err instanceof Error ? err : new Error('Failed to save equipment');
          setError(errorObj);
          setStatus('error');
          if (savedItems.length > 0) {
            queryClient.invalidateQueries({ queryKey: ['equipment', selectedTank.id] });
          }
          notifications.show({
            title: 'Error',
            message: errorObj.message,
            color: 'red',
          });
          return { savedItems, savedTempIds, error: errorObj };
        }
      }

      // Invalidate equipment queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['equipment', selectedTank.id] });

      setStatus('success');
      notifications.show({
        title: 'Success',
        message: `${totalItemsToCreate} equipment item${totalItemsToCreate === 1 ? '' : 's'} added`,
        color: 'green',
      });

      return { savedItems, savedTempIds };
    },
    [selectedTank, queryClient]
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setSavedCount(0);
    setTotalCount(0);
  }, []);

  return {
    saveEquipment,
    reset,
    isIdle: status === 'idle',
    isSaving: status === 'saving',
    isSuccess: status === 'success',
    isError: status === 'error',
    error,
    savedCount,
    totalCount,
  };
}
